import type { FocalPoint } from '@/lib/focal-point';

export interface SymposiumPhoto {
    url: string;
    focalPoint?: FocalPoint;
    credit?: string;
    alt: string;
}

/**
 * Symposium photo manifest.
 *
 * Sources: Symposium folder in the Design Studio Image Library (folder_id=4).
 * Focal points use 0–100 percentage scale (CSS object-position semantics).
 */
export const SYMPOSIUM_PHOTOS = {
    splashLeft: {
        url: 'https://cdnweb.luxrallytravel.com/design-studio/images/2026/04/56aa9a11-1430-4714-94e4-c660d7b750be.jpg',
        focalPoint: { x: 50, y: 45 },
        alt: 'LuxRally advisors in conversation at the 2025 Symposium',
    },
    splashRight: {
        url: 'https://cdnweb.luxrallytravel.com/design-studio/images/2026/04/99cd0e6f-6377-44e9-b01d-cffef6afc994.jpg',
        focalPoint: { x: 55, y: 42 },
        alt: 'Brand partner at the 2025 Symposium',
    },
    agentHero: {
        url: 'https://cdnweb.luxrallytravel.com/design-studio/images/2026/04/360be620-99d6-4368-a969-6430f4396235.jpg',
        focalPoint: { x: 50, y: 50 },
        alt: 'The full conference room at the 2025 Symposium',
    },
    vendorHero: {
        url: 'https://cdnweb.luxrallytravel.com/design-studio/images/2026/04/360be620-99d6-4368-a969-6430f4396235.jpg',
        focalPoint: { x: 50, y: 50 },
        alt: 'The full conference room at the 2025 Symposium',
    },
    closing: {
        url: 'https://cdnweb.luxrallytravel.com/design-studio/images/2026/04/ee33994f-3d0e-4efd-93a1-c865f6691f93.jpg',
        focalPoint: { x: 50, y: 42 },
        alt: 'The team gathered at the 2025 Symposium',
    },
    chandeliers: {
        url: 'https://cdnweb.luxrallytravel.com/design-studio/images/2026/04/15b4586a-d076-4d85-a2a8-39367dfdd644.jpg',
        focalPoint: { x: 50, y: 35 },
        alt: 'Crystal chandeliers above the ballroom',
    },
    recap: [
        // Pre-resized to 1600px wide (originals are 6000px). Browser was
        // downsampling 12x and amplifying noise; serving an appropriately
        // sized source eliminates the grain.
        {
            url: 'https://cdnweb.luxrallytravel.com/design-studio/images/2026/04/78f41174-5303-4e48-a088-9f0d9b86af00.jpg',
            focalPoint: { x: 50, y: 40 },
            alt: 'Advisors in conversation at an evening event',
        },
        {
            url: 'https://cdnweb.luxrallytravel.com/design-studio/images/2026/04/a1be277b-6958-4be0-ab63-21ce2c2f77de.jpg',
            focalPoint: { x: 50, y: 40 },
            alt: 'LuxRally team at the 2025 Symposium',
        },
    ] as const,
    // 2026 venue: Four Seasons Hotel Miami (1435 Brickell Avenue). Drop the
    // property photo into the Symposium Image Library folder (folder_id=4) and
    // set `url` below to its CDN address — VenueSpotlight renders the figure
    // only when `url` is non-empty, so the page stays clean until then.
    fourSeasonsMiami: {
        url: '',
        focalPoint: { x: 50, y: 50 },
        alt: 'Four Seasons Hotel Miami on Brickell Avenue',
    },
    venue: [
        {
            url: 'https://cdnweb.luxrallytravel.com/design-studio/images/2026/04/7c9039c3-09dd-4ec5-8aa6-8044c6953a5f.jpg',
            focalPoint: { x: 50, y: 55 },
            alt: 'A landmark Miami resort exterior',
        },
        {
            url: 'https://cdnweb.luxrallytravel.com/design-studio/images/2026/04/6ef06e04-0483-4e19-b275-4abc5a5ad486.jpg',
            focalPoint: { x: 50, y: 50 },
            alt: 'A conference room set for the LuxRally Travel Symposium',
        },
    ] as const,
} as const;

export const SYMPOSIUM_DETAILS = {
    dateRange: 'August 20–23, 2026',
    dateRangeShort: 'Aug 20–23',
    venue: 'Four Seasons Hotel Miami',
    venueAddress: '1435 Brickell Avenue, Miami, FL 33131',
    venueUrl: 'https://www.fourseasons.com/miami/',
    city: 'Miami, FL',
    estimatedCost: 1400,
    estimatedCostLabel: '$1,400 single occupancy',
    legacyUrl: '/2026-symposium',
    issueLabel: 'Vol. I',
    issueTitle: 'The 2026 Symposium',
    issueDate: 'August 20—23, 2026',
} as const;

/**
 * The four-day rhythm. Thursday is pre-arrival/site-tour, Friday and Saturday
 * are the main symposium days, Sunday is open.
 */
export const SYMPOSIUM_ITINERARY = [
    { day: 'Thursday', date: 'Aug 20', label: 'Arrival & site tours', detail: 'Top producers arrive; an informal site tour for everyone else.' },
    { day: 'Friday', date: 'Aug 21', label: 'Symposium', detail: 'Main programming, partner sessions, evening dinner.' },
    { day: 'Saturday', date: 'Aug 22', label: 'Symposium', detail: 'Workshops, the awards dinner, late-night gatherings.' },
    { day: 'Sunday', date: 'Aug 23', label: 'Open & departure', detail: 'No formal programming — depart, or stay at your own pace.' },
] as const;
